import { formatBsDate } from '@/lib/bsDate'
import type { Announcement } from '@/schemas/company-profile'
import { typeLabel } from '@/screens/stock/lib/noticeType'

/**
 * How many notices "News about {symbol}" asks for — the main column's four
 * rows. The Stock rail asks for the same page and draws three of it, so on
 * Overview both blocks read one cache entry and the page makes one request.
 */
export const NOTICE_PAGE_SIZE = 4

/** The rail's own row count for "News about {symbol}" (`4236:73517`). */
export const RAIL_NOTICE_ROWS = 3

/**
 * "16 Aswin 2083, Dividend": the BS date, then the notice's type.
 *
 * `type` is free text and legacy rows are SHOUTED ("RIGHT SHARE"), so it is
 * spelled by `typeLabel`, the same one the Announcements tab's badge uses: one
 * notice must not read two ways on two tabs.
 */
export const noticeMetaParts = (notice: Announcement): Array<string | undefined> => [
  formatBsDate(notice.date),
  notice.type === null ? undefined : typeLabel(notice.type),
]

export const noticeMeta = (notice: Announcement): string =>
  noticeMetaParts(notice)
    .filter((part) => part !== undefined)
    .join(', ')

/**
 * The stock tab a notice belongs to, for the rail's rows (the mock's rows open Dividend, Reports or
 * Events by the notice's kind). Read off the free-text `type`, then the title: a dividend, bonus or
 * right share opens Dividend; results and reports open Reports; an AGM, SGM or book closure opens
 * Events; anything else, Announcements.
 */
export const noticeTab = (
  notice: Announcement,
): 'dividend' | 'reports' | 'events' | 'announcements' => {
  const text = `${notice.type ?? ''} ${notice.title}`.toLowerCase()
  if (/dividend|bonus|right share/.test(text)) return 'dividend'
  if (/agm|sgm|egm|general meeting|book clos/.test(text)) return 'events'
  if (/report|result|quarter|financial|profit/.test(text)) return 'reports'
  return 'announcements'
}
